/* One library row, at the two widths that have a host.
 *
 * ·  320px  `drawer`, the programator's — and the calendar dock's since 19 September 2026: a 48×36
 *           plate inside a hairline, the name on ONE line, and ONE chip under it — the primary
 *           muscle, TINTED in its own hue. The mock reads a row by its muscle first, which a run of
 *           quiet grey type words cannot be read by.
 * ·  460px  `dialog`, the survey editor's recording picker (owner, 17 September 2026): the drawer's
 *           plate and card, and a "typ · sprzęt" line. **No muscle caption.**
 *
 * A 240px `compact` row stood here until the calendar's dock took the drawer; `.itemWide` is what
 * carried the difference and both remaining variants are wide, so only the muscle tag's own layout
 * is `.itemDrawer`'s. The two differ in JSX, not in box.
 *
 * The 02.08 gallery's 460px rail row — a 76×50 plate, a type pill, the bare muscle dot, the
 * equipment line and a `+` box — is gone (13 September 2026). Its one host was the old fullscreen
 * day, which the owner's day modal replaced, and the modal draws the drawer.
 *
 * The row is ONE button. Pressing it acts, `E` on it acts, and dragging it — where the plan builder
 * binds a gesture to the `<li>` — inserts at the position it is dropped: three paths, one act, and
 * no second control inside the row to steal a press from any of them. */

.item {
  display: flex;
  min-width: 0;
}

.row {
  display: flex;
  flex: 1;
  gap: var(--space-2);
  align-items: center;
  min-width: 0;
  padding: var(--space-7px) var(--space-2);
  text-align: left;
  background: var(--bg-card);
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: transparent;
  border-radius: var(--radius-sm);
}

/* The WIDE box, drawn by the drawer and the dialog alike — the mock's `9px` gutters at gap 11 and
 * a 12px radius, on `--bg-card`
 * inside a hairline. The edge is what makes a row a CARD on the drawer's sunken ground; it was
 * transparent, over a list painted `--bg-card`, so the whole result set drew as one sheet.
 *
 * ── The vertical padding is the mock's 7 taken to 3 (owner, 16 September 2026) ────────────────
 * "Zgrupienie nazwy ćwiczeń": the list has to hold more movements without reading as a squeeze. The
 * TAG is what shrank (see `.muscleChip`), and this is the row closing around it.
 *
 * **The plate is the floor, and that is the whole arithmetic here.** 48×36 is the mock's and the
 * owner kept it, so a row cannot be shorter than 36 + 2×padding + 2 of border however small the text
 * gets — the text stack is 30px and never reaches it. At the mock's 7 that is 52px; at 3 it is 44.
 * Measured in the drawer at 1440×900, on a 721px scroller: **13 rows fully visible before, 15
 * after.** Every px below 3 is the card's edge touching its own plate, and the row is still 44px of
 * hit area, four above the 40 this app owes. */
.itemWide .row {
  gap: var(--space-11px);
  padding: var(--space-3px) var(--space-9px);
  border-color: var(--border);
  border-radius: var(--radius-12px);
  transition:
    background var(--dur-fast) var(--ease-in-out),
    border-color var(--dur-fast) var(--ease-in-out);
}

.row:hover {
  background: var(--bg-inset);
}

.row:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-1px);
}

/* The mock's hover: `--bg-chip` inside `--border-2`, both edges of the card moving together, over
 * its own `.16s`. The duration is the app's `--dur-fast` (120ms) rather than a token minted for one
 * declaration — every other hover tint in this app already settles at that speed, and 40ms on a
 * colour fade is not a difference a trainer can see.
 *
 * Its own rule rather than the shared `.row:hover` below, which paints `--bg-inset`: the drawer's
 * list stands on a SUNKEN ground where `--bg-inset` is barely a change, and its rows carry a real
 * border that the two columns beside a day do not. */
.itemWide .row:hover {
  background: var(--bg-chip);
  border-color: var(--border-2);
}

/* The source while a pick is in flight — the gallery's own dashed, emptied treatment. Dimmed
 * rather than removed: the rail is a scrolling list, and pulling the row out would drag every
 * result below it up under the pointer. */
.item[data-dragging] .row {
  background: transparent;
  border-style: dashed;
  border-color: var(--border-2);
  opacity: var(--drag-source-opacity);
}

/* Always painted, image or none — a catalogue where only some movements carry a still would
 * otherwise render rows of two different heights. `--bg-inset` is the ground under a photograph
 * that has not decoded, 404s or is blocked, and the ground of an unattributed movement's tile. */
.photo {
  flex: none;
  width: 34px;
  height: 22px;
  object-fit: cover;
  background: var(--bg-inset);
  border-radius: var(--radius-5px);
}

/* A movement with no photograph. The tile is filled with its own muscle hue at 13% and carries the
 * barbell in that hue — both per ROW, so both arrive as inline style; what is here is the box.
 *
 * BOTH widths, not the drawer alone: the plate is the same size whether it holds a photograph or
 * not, so nothing about either layout moves, and the same 479-row catalogue feeds both hosts. A
 * movement drawn as a tile in the builder and as an empty rectangle in the calendar's day editor
 * would be one library answering twice. */
.photoFallback {
  display: grid;
  place-items: center;
}

/* The mock scales the drawing with the plate — 20px on 48x36. The 34x22 plate has no mock of its
 * own, so it takes the same share of its own width. */
.photoGlyph {
  display: block;
  width: 14px;
  height: 14px;
}

.text {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-1px);
  min-width: 0;
}

.name {
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-heading);
  color: var(--text);
  overflow-wrap: anywhere;
}

.meta {
  display: block;
  gap: var(--space-6px);
  align-items: center;
  min-width: 0;
  overflow: hidden;
  font-size: var(--fs-badge);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The mock's 6x6 at radius 2, inside the muscle chip — the only place a dot is drawn since the
 * wide row's bare one went with the 460px variant. */
.dot {
  display: block;
  flex: none;
  width: 6px;
  height: 6px;
  border-radius: var(--radius-hair);
}

.detail {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}


/* ── The wide row (07.09 programator) ─────────────────────────────────────────────────────────
 * The mock's own numbers: a 48x36 plate inside a hairline at radius 9, the name at 12.5/600/1.2 on
 * ONE line. The muscle chip under it is the DRAWER's alone; the dialog draws the detail line. */

.itemWide .photo {
  width: 48px;
  height: 36px;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-mark);
}

.itemWide .photoGlyph {
  width: 20px;
  height: 20px;
}

/* ONE line, ellipsised. It wrapped, and a 320px column full of two-line names is what cost the list
 * its rhythm: every row a different height, and no two plates on a grid. The full name stays
 * reachable — the row's own `title` reads "Dodaj do dnia: <name>" verbatim, and an ellipsis is a
 * visual clip that leaves the accessible name whole, so a screen reader still hears all of it.
 *
 * ── 12.5 → 13 (owner, 16 September 2026) ─────────────────────────────────────────────────────
 * The round that shrank the muscle tag put the name UP one step, to `--fs-exercise`, which is the
 * step this app already sets an exercise's name at everywhere else in the builder. The name is what
 * a trainer scans a 477-row list by; the tag beneath it is a facet they have usually already
 * filtered on. Those two were 12.5 against an uppercase, tracked, bold 10 — close enough that the
 * tag read as the louder of the pair. 13 against 9.5 is the order the list is actually read in. */
.itemWide .name {
  font-size: var(--fs-exercise);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-display);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* `.meta` is a BLOCK with `nowrap` by default; the drawer takes the flex row back for its chip. */
.itemDrawer .meta {
  display: inline-flex;
  gap: var(--space-5px);
  overflow: visible;
  white-space: normal;
}

/* The primary group: a dot in its hue and its word, and NO pill (owner, 15 September 2026, which
 * withdrew the mock's tinted 19px box). The DOT carries the hue and the word carries the meaning, so
 * nothing here is colour-only.
 *
 * `color` is not declared here, and that is not an omission: it is the ROW's hue and arrives as
 * inline style. The mock's ink rule is per theme — the hue in dark, the
 * neutral `--text-2` in light — and it is resolved through `--chip-tint-ink-hue`, declared once in
 * `design-system/tokens/index.css`. A chip with no hue is not drawn at all.
 *
 * ── The tag loses a third, and the row closes around it (owner, 16 September 2026) ────────────
 * "Zmniejsz podpisy ćwiczeń ... kosztem zgrupienia nazwy ćwiczeń." The tag is what shrinks; the
 * name went the other way, one step up.
 *
 * **A literal third of the type is not available, and that is a real constraint rather than a
 * rounding.** 10px × ⅔ is 6.7px, and the 12.08 typography audit set this system's floor at 10 —
 * walking `--fs-nav`, `--fs-tag` and `--fs-badge` UP to it for exactly this reason. The only step
 * below is `--fs-micro-label` at 9.5, this app's half-step, and that is what the glyphs take. So
 * the third comes off the tag's BLOCK, which is the part a trainer actually reads as its size: the
 * line box goes 19px → 13px, a third exactly, and the dot 6px → 5px with it. A 19px line under a
 * 13px name was the tag claiming a row of its own; 13px sits it under the name as a caption.
 *
 * At 9.5px uppercase, `--ls-chip` tracking and `--fw-bold`, against `--bg-card`, the word is still
 * a word: the dot carries the hue and the letters carry the group, which is the 15.09 arrangement
 * unchanged. Nothing here became colour-only and nothing became a glyph. */
.muscleChip {
  display: inline-flex;
  flex: none;
  gap: var(--space-1);
  align-items: center;
  height: 13px;
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-chip);
  text-transform: uppercase;
  white-space: nowrap;
}

/* The dot shrinks with the word it belongs to — 6px beside a 9.5px cap height read as a bullet
 * rather than as part of the tag. It stays a circle at `--radius-hair`, and it is still the only
 * place this row draws one. */
.itemDrawer .dot {
  width: 5px;
  height: 5px;
}

/* ── The tick box (16 September 2026) ─────────────────────────────────────────────────────────
 *
 * The mock's `checkSt`: an 18px square at radius 5, an empty `--border-2` outline until the row is
 * chosen and a `--text` fill with a `--bg-card` tick once it is. It stands at the row's leading
 * edge, before the plate, so a column of ticks reads down the list.
 *
 * The state is not colour alone: the TICK is a shape that is either drawn or not, and the button
 * itself carries `aria-pressed`. */
.check {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--sz-lib-check);
  height: var(--sz-lib-check);
  color: var(--bg-card);
  background: transparent;
  border: var(--border-thick) solid var(--border-2);
  border-radius: var(--radius-5px);
  transition:
    background var(--dur-fast) var(--ease-in-out),
    border-color var(--dur-fast) var(--ease-in-out);
}

.row[aria-pressed='true'] .check {
  background: var(--text);
  border-color: var(--text);
}

.check svg {
  display: block;
  width: var(--sz-lib-tick);
  height: var(--sz-lib-tick);
}

/* Drawn only on a chosen row. `visibility` rather than a conditional render, so the box never
 * changes size and the row's text cannot shift by a pixel as the trainer works down the list. */
.row:not([aria-pressed='true']) .check svg {
  visibility: hidden;
}
